// シーズン装飾の適用判定。サーバーから受け取った「現在有効なシーズン装飾」と、
// 前回取得した結果（端末に保存）から、勤務確定モーダル・フッターに出すテーマを決める

// このアプリが装飾を持っているテーマ種別。サーバーが新しい種別を返しても、ここに無ければ通常デザインにする
export const SUPPORTED_SEASON_THEMES = ["halloween", "christmas"] as const;
export type SeasonTheme = (typeof SUPPORTED_SEASON_THEMES)[number];

export type SeasonDecoration = {
  theme: string;
  // YYYY-MM-DD（JST の日付。終了日当日も表示期間に含む）
  startOn: string;
  endOn: string;
};

// undefined: 保持していない / null: 前回の取得結果が「なし」
export type CachedSeasonDecoration = SeasonDecoration | null | undefined;

const STORAGE_KEY = "season_decoration";
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const JST_OFFSET_MS = 9 * 60 * 60 * 1000;

function isSupportedTheme(theme: string): theme is SeasonTheme {
  return (SUPPORTED_SEASON_THEMES as readonly string[]).includes(theme);
}

/** 端末時刻を JST に換算した日付（YYYY-MM-DD） */
export function toJstDateString(now: Date): string {
  return new Date(now.getTime() + JST_OFFSET_MS).toISOString().slice(0, 10);
}

/**
 * 出すテーマを決める（上から順に評価）
 * - 取得できた: 「なし」なら通常／未対応の種別なら通常（取得失敗としては扱わない）／対応済みなら装飾
 * - 取得できていない（失敗・未ログイン・取得中）: 前回の結果があれば同じ規則で判定し、無ければ通常
 * どちらの場合も、今日（JST）が表示期間の外なら通常デザインにする
 */
export function resolveSeasonTheme({
  fetched,
  cached,
  today,
}: {
  // 取得できた結果。取得できていなければ undefined
  fetched: SeasonDecoration | null | undefined;
  cached: CachedSeasonDecoration;
  today: string;
}): SeasonTheme | null {
  const decoration = fetched !== undefined ? fetched : cached;
  if (!decoration) return null;
  if (!isSupportedTheme(decoration.theme)) return null;
  // YYYY-MM-DD どうしは文字列の大小で日付の前後を比べられる
  if (today < decoration.startOn || today > decoration.endOn) return null;
  return decoration.theme;
}

function isSeasonDecoration(value: unknown): value is SeasonDecoration {
  if (typeof value !== "object" || value === null) return false;
  const { theme, startOn, endOn } = value as Record<string, unknown>;
  return (
    typeof theme === "string" &&
    typeof startOn === "string" &&
    DATE_PATTERN.test(startOn) &&
    typeof endOn === "string" &&
    DATE_PATTERN.test(endOn)
  );
}

/** 前回取得した結果を読む。保存されていない・壊れている・読めないときは undefined */
export function readCachedSeasonDecoration(): CachedSeasonDecoration {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return undefined;
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null) return null;
    return isSeasonDecoration(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

/** 取得できた結果を保存する（次に取得できなかったときに使う）。保存できなくても表示は妨げない */
export function writeCachedSeasonDecoration(
  decoration: SeasonDecoration | null,
): void {
  try {
    const value = decoration
      ? {
          theme: decoration.theme,
          startOn: decoration.startOn,
          endOn: decoration.endOn,
        }
      : null;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // プライベートブラウズ等で保存できない場合は、前回の結果を持たないだけ
  }
}
